import { FC, useMemo } from "react";

import {
  NOTIFICATION_ACTION_TYPES,
  createNotificationAction,
} from "shared/ui/notifications/notification-generic/base-parts/actions";
import { PropsWithClassNameI } from "shared/lib/interfaces/ui";

import { NotificationBase } from "@/modules/notifications/notification/base";
import { getDialerHrefFor } from "@/modules/manage-pipeline/lib/links";
import {
  NotificationI,
  PipelineReminderNotificationI,
} from "@/lib/types/notification";

type NotificationPipelineReminderPropsI = NotificationI & PropsWithClassNameI;

/** The LGA's sentence; a fallback if an older row has none. */
export const describePipelineReminder = (
  reminder: PipelineReminderNotificationI | undefined
): string =>
  reminder?.message?.trim() ||
  `${reminder?.lga_name?.trim() || "Your LGA"} sent you a reminder about ${reminder?.contact_name?.trim() || "a prospect"}${reminder?.campaign_name?.trim() ? ` for ${reminder.campaign_name.trim()}` : ""}.`;

/** The dialer on the prospect; null when an older row lacks the ids. */
export const getPipelineReminderHref = (
  reminder: PipelineReminderNotificationI | undefined
): string | null =>
  reminder?.campaign_id && reminder.account_id && reminder.contact_id
    ? getDialerHrefFor({
        campaignId: reminder.campaign_id,
        accountId: reminder.account_id,
        contactId: reminder.contact_id,
        listId: reminder.list_id,
      })
    : null;

/**
 * PipelineReminder (C-16 S3): an LGA, from Team → Pipeline, asks the rep to
 * follow up on a no-show, make a reminder call or do a warm handoff.
 * Opens the dialer on the contact, as Command Center's reminder link did.
 */
export const NotificationPipelineReminder: FC<
  NotificationPipelineReminderPropsI
> = (props) => {
  const { id, is_seen, created_at, className, pipeline_reminder } = props;
  const text = useMemo(
    () => describePipelineReminder(pipeline_reminder),
    [pipeline_reminder]
  );
  const href = getPipelineReminderHref(pipeline_reminder);

  return (
    <NotificationBase
      id={id}
      isUnread={!is_seen}
      className={className}
      avatar={{
        ...props,
        campaign_id: pipeline_reminder?.campaign_id,
        campaign_name: pipeline_reminder?.campaign_name,
      }}
      description={{ text: <>{text}</>, date: created_at }}
      actions={
        href
          ? [
              createNotificationAction(
                NOTIFICATION_ACTION_TYPES.PRIMARY,
                "Open in dialer",
                () => window.location.assign(href)
              ),
            ]
          : []
      }
    />
  );
};
